import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormData } from '../../../contexts/FormDataContext';
import { apiFetch } from '../../../api/client';

type SymptomOption = {
  symptom_id: number;
  symptom_name: string;
  definition?: string | null;
  confidence?: number;
  matched_query?: string | null;
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const SYMPTOM_KEY_TO_LABEL: Array<{ key: string; label: string }> = [
  { key: 'symptom_unexplained_weight_loss', label: 'Unexplained weight loss' },
  { key: 'symptom_abdominal_pain', label: 'Abdominal pain' },
  { key: 'symptom_blurred_vision', label: 'Blurred vision / vision changes' },
  { key: 'symptom_increased_thirst', label: 'Increased thirst' },
  { key: 'symptom_irritable_mood', label: 'Feeling irritable or other mood changes' },
  { key: 'symptom_hyperglycemia', label: 'Hyperglycemia' },
  { key: 'symptom_headache', label: 'Headache' },
  { key: 'symptom_fatigue', label: 'Fatigue' },
  { key: 'symptom_recurrent_infections', label: 'Recurrent infections' },
  { key: 'symptom_polyuria', label: 'Polyuria / frequent urination' },
  { key: 'symptom_new_bed_wetting', label: 'New bed wetting' },
  { key: 'symptom_very_hungry', label: 'Feeling very hungry' },
  { key: 'symptom_poor_wound_healing', label: 'Poor wound healing' },
  { key: 'symptom_fruity_breath', label: 'Fruity Smelling Breath' },
  { key: 'symptom_nausea_vomiting', label: 'Nausea or vomiting' },
  { key: 'symptom_genital_itching', label: 'Genital itching' },
  { key: 'symptom_yeast_infection_prepuberty', label: 'Yeast infection pre-puberty (females only)' },
  { key: 'symptom_menstrual_changes', label: 'Menstrual changes (females only)' },
];

const LABEL_TO_KEY = new Map<string, string>(
  SYMPTOM_KEY_TO_LABEL.map((item) => [item.label.toLowerCase(), item.key]),
);

const FALLBACK_OPTIONS: SymptomOption[] = SYMPTOM_KEY_TO_LABEL.map((item, idx) => ({
  symptom_id: -(idx + 1),
  symptom_name: item.label,
  definition: null,
}));

interface SymptomAjaxMultiSelectProps {
  tabName: string;
}

function normLabel(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveFieldKeyFromName(symptomName: string): string | undefined {
  const nameNorm = normLabel(symptomName);
  if (!nameNorm) return undefined;

  const direct = LABEL_TO_KEY.get(nameNorm);
  if (direct) return direct;

  for (const { key, label } of SYMPTOM_KEY_TO_LABEL) {
    const labelNorm = normLabel(label);
    if (nameNorm === labelNorm || nameNorm.includes(labelNorm) || labelNorm.includes(nameNorm)) {
      return key;
    }
  }
  return undefined;
}

function getRecentTranscriptSnippet(): string {
  const payload = (window as any).__whisperLiveKitData;
  if (!payload) return '';

  const lines = Array.isArray(payload.full_lines)
    ? payload.full_lines
    : Array.isArray(payload.lines)
      ? payload.lines
      : [];

  const texts = lines
    .slice(-8)
    .map((line: any) => {
      if (typeof line === 'string') return line;
      if (line && typeof line === 'object') return String(line.text ?? line.content ?? line.line ?? '');
      return '';
    })
    .map((x: string) => x.trim())
    .filter(Boolean);

  return texts.join(' ').slice(-1200).trim();
}

export default function SymptomAjaxMultiSelect({ tabName }: SymptomAjaxMultiSelectProps) {
  const { getFieldValue, updateField } = useFormData();
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SymptomOption[]>(FALLBACK_OPTIONS);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestedSymptoms, setSuggestedSymptoms] = useState<SymptomOption[]>([]);
  const lastTranscriptFingerprintRef = useRef('');

  const selected = useMemo(() => {
    const selectedLabels = SYMPTOM_KEY_TO_LABEL
      .filter(({ key }) => Boolean(getFieldValue(tabName, key, false)))
      .map(({ label }) => label.toLowerCase());
    const selectedSet = new Set(selectedLabels);
    return options.filter((opt) => selectedSet.has(opt.symptom_name.toLowerCase()));
  }, [getFieldValue, options, tabName]);

  useEffect(() => {
    const q = inputValue.trim();
    const timer = window.setTimeout(() => {
      setLoading(true);
      void apiFetch(
        `${API_BASE_URL}/api/symptoms/search?q=${encodeURIComponent(q)}&limit=50`,
      )
        .then((res) => (res.ok ? res.json() : []))
        .then((rows: SymptomOption[]) => {
          const fetched = Array.isArray(rows) ? rows : [];
          const merged = [...fetched];
          const existing = new Set(merged.map((x) => x.symptom_name.toLowerCase()));
          for (const fallback of FALLBACK_OPTIONS) {
            if (!existing.has(fallback.symptom_name.toLowerCase())) {
              merged.push(fallback);
            }
          }
          setOptions(merged);
        })
        .catch(() => {
          setOptions(FALLBACK_OPTIONS);
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      const snippet = getRecentTranscriptSnippet();
      const fingerprint = snippet.toLowerCase();
      if (snippet.length < 12 || fingerprint === lastTranscriptFingerprintRef.current) return;
      lastTranscriptFingerprintRef.current = fingerprint;

      setSuggestLoading(true);
      void apiFetch(`${API_BASE_URL}/api/symptoms/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symptom_text: snippet.slice(-400).trim(),
          transcript_context: snippet,
          top_k: 6,
        }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((payload) => {
          const candidates = Array.isArray(payload?.candidates) ? payload.candidates : [];
          const filtered = candidates.filter((c: SymptomOption) => Number(c.confidence || 0) >= 0.35);
          setSuggestedSymptoms(filtered);
        })
        .catch(() => {
          setSuggestedSymptoms([]);
        })
        .finally(() => setSuggestLoading(false));
    }, 4000);

    return () => window.clearInterval(tick);
  }, []);

  const handleChange = (_: unknown, next: SymptomOption[]) => {
    const selectedKeys = new Set<string>();
    for (const opt of next) {
      const key = resolveFieldKeyFromName(opt.symptom_name);
      if (key) selectedKeys.add(key);
    }

    for (const { key } of SYMPTOM_KEY_TO_LABEL) {
      updateField(tabName, key, selectedKeys.has(key));
    }
  };

  const applySuggestedSymptom = (opt: SymptomOption) => {
    const key = resolveFieldKeyFromName(opt.symptom_name);
    if (!key) return;
    updateField(tabName, key, true);
  };

  return (
    <Box>
      <Autocomplete
        multiple
        options={options}
        value={selected}
        loading={loading}
        filterSelectedOptions
        isOptionEqualToValue={(a, b) => a.symptom_name.toLowerCase() === b.symptom_name.toLowerCase()}
        getOptionLabel={(o) => o.symptom_name}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        renderOption={(props, option) => (
          <li {...props} key={`${option.symptom_id}-${option.symptom_name}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
              <Typography variant="body2">{option.symptom_name}</Typography>
              {typeof option.confidence === 'number' && option.confidence > 0 ? (
                <Chip
                  size="small"
                  label={`${Math.round(option.confidence * 100)}%`}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                  color={option.confidence >= 0.7 ? 'success' : option.confidence >= 0.45 ? 'warning' : 'default'}
                />
              ) : null}
            </Box>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${option.symptom_id}-${option.symptom_name}`}
              label={option.symptom_name}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search or select symptoms..."
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {suggestedSymptoms.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {suggestedSymptoms.map((s) => (
            <Chip
              key={`sg-${s.symptom_id}-${s.symptom_name}`}
              label={`${s.symptom_name} (${Math.round(Number(s.confidence || 0) * 100)}%)`}
              size="small"
              variant="outlined"
              onClick={() => applySuggestedSymptom(s)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
